/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormValues, schema, useMergedContract } from './schema';

import { handleTrade } from './TradeConfirmationModal';
import {
  ContractInfo,
  ContractPrices,
  GetHotTokensParams,
  HotTokensRowInfo,
  HotTokensSortKeys,
  PoolInfo,
} from 'api/types/httpsTypes/contracts';
import { NotificationDex } from '../components/alerts/notification';
import { DexTradeTemplate } from 'api/types/httpsTypes/dex-trade';
import { formatAddress } from 'modules/ethereum/utils';
import { useUserState } from 'modules/user/UserContext';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  dexTradeKeys,
  DexTradeService,
} from 'api/services/httpServices/DexTradeService';
import { TemplatesBar } from './components/templates/TemplatesBar';
import { mizarTemplates } from './components/templates/mizar-templates';
import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import config from 'config';
import { useHapticFeedback } from 'modules/telegram/core/useHapticFeedback';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { useSnackBar } from 'modules/layouts/SnackBar/context';
import { formatApiError } from 'helpers/api/apiErrors';
import Button from 'modules/shared-components/button/button';
import { HotTokensPeriods } from '../research/components/Wallets/usePageParams';

import PageTemplate from '../components/layout';
import { TradeForm } from './components/tokenInfo/tradeForm';
import {
  AnalyticsKeys,
  TokenInfoTradeBody,
  useSchemaPage,
} from './components/tokenInfo/tokenInfoTradeBody';
import { useCacheLastVisitedAddress } from './components/tokenInfo/useChaceLastVisitedAddress';
import { useDefaults } from './components/tokenInfo/positionMapping';
import { IntroBlocker } from './components/tokenInfo/introBlocker';
import { Chains } from 'api/types/httpsTypes/d-wallets';

import { useEffect, useMemo, useRef, useState } from 'react';
import { TokenNameHeaderMobile } from './components/tokenInfo/TokenCardHeader';
import { formatNumberWithSuffix, safeBig } from 'utils/FormatNumber';
import { useGlobalChain } from 'modules/routing/chainProvider';
import { TerminalPageMobile } from '../side-panels/TerminalPageMobile';
import { useQueryParams } from 'modules/routing/useQueryParams';
import { SwapsTerminal } from './stats/swaps';
import { HoldersComponent } from './stats/holders';
import { duration } from 'moment';
import { MainInfoComponent } from './stats/main-info';
import { MainInfoBody } from './stats/main-info-body';
import { useWallets } from 'modules/routing/walletProvider';
import { useToggle } from 'modules/utils/useToggle';
import { useActiveWallets } from 'modules/routing/activeWalletsProvider';
import { useLastUsedTemplate } from 'modules/routing/activeTemplateProvider';
import { chainAssetImgWhite } from 'api/types/httpsTypes/chainImg';
import { SwapsFilterProvider } from './stats/swapsFilterProvider';
import { useWatchListProvider } from 'modules/routing/watchListProvider';
import { getAuthLoginUrl } from '../../auth/utils';
import { CreateDWalletModal } from '../wallets/components/CreateDWalletModal';
import { useChartSettings } from 'modules/routing/mainSettingsProvider';
import { prefetchBars } from '../components/Chart/barsStore';
import { barsCache, getPrefetchRange } from '../components/Chart/DexChart';
import { LoadingTerminalDesktop } from './components/loadingComponent';
import { isTokenSuspiciousCheck } from '../research/components/Wallets/labels';
import { MainStatsWrapperV2 } from './stats/main-stats-v2';
import { useContractMarks } from '../components/Chart/marksManager';
import { UserV1 } from 'api/types/httpsTypes/user';
import { useStarLevel } from 'api/services/httpServices/StarLevelService';

export default function Terminal() {
  const { chain, address } = useCacheLastVisitedAddress();
  const [selectedChain, setSelectedChain] = useGlobalChain();
  const isDesktop = useMediaQuery(screenGte.large);
  const { markSettings, chart } = useChartSettings();
  const { data: wallets } = useWallets();
  const location = useLocation();
  const [lastBarTimestamp, setLastBarTimestamp] = useState<number | null>(null);

  useEffect(() => {
    if (chain !== selectedChain && document.visibilityState === 'visible') {
      setSelectedChain(chain);
    }
  }, [chain, selectedChain]);

  const { user } = useUserState();

  const {
    isLoading,
    isFetching,
    data: contractLight,
    error,
  } = useQuery({
    queryFn: () => ContractService.getContractLight(chain, address),
    queryKey: contractServiceKeys.getContractLight(chain, address),
    enabled: !!address,
    keepPreviousData: true,
  });

  // Extract makerAddress from URL (e.g., ?makerAddress=0xABC...)
  const makerAddress = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('maker') || undefined;
  }, [location.search]);

  // Build params for API call
  const stableChain = useMemo(() => chain, [chain]);
  const stableAddress = useMemo(() => address, [address]);
  const stableWallets = useMemo(() => wallets, [wallets]);

  const markSettingsParams = useMemo(
    () => ({
      tags: markSettings,
      interval: chart.interval,
    }),
    [markSettings, chart.interval]
  );

  const stableAvatar = useMemo(
    () => (user ? (user as UserV1).avatar_url : undefined),
    [user]
  );

  const { data: starLevel } = useStarLevel();

  const stableStarLevel = useMemo(
    () => (starLevel ? starLevel : undefined),
    [starLevel]
  );

  const { chart: chartSettings } = useChartSettings();

  const chartSettingsRef = useRef(chartSettings);

  useEffect(() => {
    chartSettingsRef.current = chartSettings;
  }, [chartSettings]);

  useEffect(() => {
    const { from, to } = getPrefetchRange(chartSettings.interval);
    const resolution = chartSettings.interval;

    if (!contractLight) return;

    const poolAddress =
      contractLight.liquidityInfo.activePools[0]?.poolAddress ?? '';

    void prefetchBars({
      poolAddress,
      chain: contractLight.generalInfo.chain.key,
      resolution,
      currencyIsUsd: chartSettings.currencyIsUsd,
      limit: 350,
      fromTimestamp: from,
      toTimestamp: to,
    }).then((bars) => {
      const cacheKey = `${
        contractLight.generalInfo.chain.key
      }:${poolAddress}:${resolution}:${
        chartSettings.currencyIsUsd ? 'usd' : 'quote'
      }`;
      barsCache.set(cacheKey, bars);
      // Solana: Bars is SolanaOHLCItem[]
      if (bars && bars.length > 0) {
        const lastBar = bars[0];
        setLastBarTimestamp(lastBar.t ? lastBar.t : null);
      } else {
        setLastBarTimestamp(null);
      }
    });
  }, [
    contractLight?.generalInfo.chain.key,
    contractLight?.liquidityInfo.activePools[0]?.poolAddress,
    chartSettings.isPriceShown,
    chartSettings.currencyIsUsd,
    chartSettings.interval,
    makerAddress,
  ]);

  const stableLastBarTimestamp = useMemo(
    () => lastBarTimestamp,
    [lastBarTimestamp]
  );

  // 5. Call the hook with fully stable inputs
  useContractMarks(
    stableChain,
    stableAddress,
    markSettingsParams,
    stableWallets,
    stableAvatar,
    stableStarLevel,
    makerAddress,
    stableLastBarTimestamp
  );

  const { data: contractMain } = useQuery({
    queryFn: () => ContractService.getContract(chain, address),
    queryKey: contractServiceKeys.getContract(chain, address),
    enabled: !!address,
    keepPreviousData: true,
  });

  const { data: contractPrices } = useQuery({
    queryFn: () => ContractService.getContractPrices(chain!, address!),
    queryKey: contractServiceKeys.getContractPrices(chain!, address!),
    enabled: !!(address && chain),
    keepPreviousData: true,
    refetchInterval: 30_000,
  });

  const { data: templates, isLoading: isLoadingTemplates } = useQuery({
    queryFn: () => DexTradeService.getTemplates(),
    queryKey: dexTradeKeys.getTemplates(),
    enabled: !!user,
  });

  const contract = useMergedContract(contractLight, contractMain);

  const params: GetHotTokensParams = {
    sort: HotTokensSortKeys.TotalBuyTop,
    address: contract?.generalInfo.address ?? '',
    chain: contract?.generalInfo.chain.key ?? Chains.Solana,
    period: HotTokensPeriods.TwentyFourHour,
  };

  const { data: hotTokens } = useQuery({
    queryKey: contractServiceKeys.getHotTokens(params),
    queryFn: () => ContractService.getHotTokens(params),
    keepPreviousData: true,
    enabled:
      !!contract?.generalInfo.chain.key && !!contract?.generalInfo.address,
  });

  const { state } = location as {
    state?: {
      from: string;
    };
  };
  const from = state?.from;

  const currentPage = {
    href: `/dex/snipe/new/${chain}/${address}`,
    name: contract?.generalInfo.symbol || formatAddress(address, 3),
  };

  if (!isDesktop) {
    return (
      <PageTemplate
        pages={
          from
            ? [{ href: from, name: 'Research' }, currentPage]
            : config.isTelegramBrowser
            ? [
                { href: '/dex/navigation', name: 'Navigation' },
                { href: '/dex/navigation', name: 'Navigation' },
              ]
            : [currentPage]
        }
      >
        <TerminalPageMobile
          isLoadingTemplates={isLoadingTemplates}
          isLoading={isLoading}
          error={error}
          contract={contract}
        >
          {contract && (
            <TerminalComponent
              key={
                contract.generalInfo.address +
                '_' +
                contract.generalInfo.chain.key
              }
              templates={templates || []}
              isFetching={isFetching}
              disabled={contract ? contract.status === 'RUG_PULL' : false}
              contract={contract}
              hotTokens={hotTokens}
            />
          )}
        </TerminalPageMobile>
      </PageTemplate>
    );
  }

  if (isLoading || (user && isLoadingTemplates)) {
    return <LoadingTerminalDesktop />;
  }

  if (error) {
    return (
      <div className="xxs:m-2">
        <NotificationDex errorMessage={error} type="error">
          Could not load contract data.
        </NotificationDex>
      </div>
    );
  }

  return (
    <IntroBlocker>
      <PageTemplate
        pages={
          from
            ? [
                {
                  href: from,
                  name: 'Research',
                },
                currentPage,
              ]
            : [currentPage]
        }
      >
        {contract && (
          <div className="flex flex-col w-full  border-tailwind xxs:border xxs:border-dex-black-700">
            <TerminalComponent
              contractPrices={contractPrices}
              key={
                contract.generalInfo.address +
                '_' +
                contract.generalInfo.chain.key
              }
              templates={templates || []}
              isFetching={isFetching}
              disabled={contract ? contract.status === 'RUG_PULL' : false}
              contract={contract}
              hotTokens={hotTokens}
            />
          </div>
        )}
      </PageTemplate>
    </IntroBlocker>
  );
}

export const TabsButton = ({
  label,
  keyValue,
  isVisible = true,
  extraContent,
  className,
}: {
  label: string;
  keyValue?: AnalyticsKeys;
  isVisible?: boolean;
  extraContent?: any;
  className?: string;
}) => {
  const [queryParams, setQueryParams] = useQueryParams(useSchemaPage());
  if (!isVisible) return null;

  const isActive =
    (!queryParams.activeAnalytics && !keyValue) ||
    queryParams.activeAnalytics === keyValue;

  return (
    <Button
      type="button"
      onClick={() => {
        setQueryParams({
          // ✅ keep maker if it exists
          ...(queryParams.maker ? { maker: queryParams.maker } : {}),
          ...(queryParams.minTime ? { minTime: queryParams.minTime } : {}),
          ...(queryParams.maxTime ? { maxTime: queryParams.maxTime } : {}),
          // ✅ always set or clear activeAnalytics
          activeAnalytics: keyValue || undefined,
        });
      }}
      className={twMerge(
        'xxs:p-2 xxs:rounded-none xxs:border-solid border-transparent border-x-0 border-t-0 ',
        isActive
          ? 'xxs:text-dex-white xxs:border-solid border-white'
          : 'text-dex-white-secondary hover:text-dex-white',
        className
      )}
    >
      <div className="flex space-x-1 items-center justify-center">
        <div className="normal-case flex xxs:space-x-1 items-center xxs:pt-1">
          <div className="">{label}</div>

          <div className="text-[10px]">{extraContent}</div>
        </div>
      </div>
    </Button>
  );
};

export function TerminalComponent({
  contract,
  hotTokens,
  isFetching,
  disabled,
  templates: customTemplates,
  contractPrices,
}: {
  contract: ContractInfo;
  isFetching: boolean;
  disabled: boolean;
  templates: DexTradeTemplate[];
  hotTokens: HotTokensRowInfo[] | undefined;
  contractPrices?: ContractPrices[] | undefined;
}) {
  const { activeWallets } = useActiveWallets();
  const history = useHistory();
  const [isCreateOpen, { open, close }] = useToggle();

  const isDesktop = useMediaQuery(screenGte.large);

  const { blacklisted } = useWatchListProvider();

  const isBlackListed =
    blacklisted &&
    blacklisted.length &&
    blacklisted?.some(
      (addr) =>
        addr.address.toLowerCase() ===
        contract.generalInfo.address.toLowerCase()
    );

  const { lastUsedTemplate: defaultTemplateId } = useLastUsedTemplate();

  const [queryParams] = useQueryParams(useSchemaPage());
  const { data: wallets } = useWallets();
  const customTemplatesMapped = customTemplates.map((template) => ({
    ...template,
    wallets: activeWallets[contract.generalInfo.chain.key],
  }));

  const allTemplates = [
    ...customTemplatesMapped,
    ...mizarTemplates(
      contract.generalInfo.chain.key,
      activeWallets[contract.generalInfo.chain.key]
    ),
  ];

  const { user } = useUserState();
  const selectedPool = contract.liquidityInfo.activePools[0] as
    | PoolInfo
    | undefined;

  const hapticFeedback = useHapticFeedback();
  const [isShortLoading, setIsShortLoading] = useState(false);
  const handleShortLoadingClick = () => {
    setIsShortLoading(true);
    setTimeout(() => setIsShortLoading(false), 100); // 100ms delay
  };

  const params = {
    tokenAddress: contract.generalInfo.address,
  };

  const {
    data: mainData,
    error,
    isLoading,
  } = useQuery({
    queryKey: contractServiceKeys.getHolders(params),
    queryFn: () => ContractService.getHolders(params),
    keepPreviousData: true,
    enabled: contract.generalInfo.chain.key === Chains.Solana,
    refetchInterval: duration(12, 'seconds').asMilliseconds(),
  });

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: useDefaults(
      contract,
      allTemplates.find((x) => x.id === defaultTemplateId) || allTemplates[0]
    ),
  });

  const { handleSubmit, watch } = form;

  const { addNewMessage } = useSnackBar();

  const { mutate } = useMutation({
    mutationFn: DexTradeService.create,
    onError: (err) => {
      if (config.isTelegramBrowser) {
        addNewMessage({
          message: formatApiError(
            err,
            'There was an error while creating your trade. Try again.'
          ),
          type: 'error',
        });
      } else {
        addNewMessage({
          type: 'error',
          message: 'There was an error while creating your trade. Try again.',
        });
      }
    },
  });

  const onSubmit = handleSubmit(
    () => {
      handleTrade(form.getValues(), mutate);
      handleShortLoadingClick();
    },
    () => {
      hapticFeedback.notification('error');
    }
  );

  const buyAmount = (
    <div className="xxs:flex xxs:space-x-1 xxs:items-center">
      <div className="max-w-[200px] truncate">
        BUY {contract.generalInfo.symbol}
      </div>
      {chainAssetImgWhite(contract.generalInfo.chain.key)}

      {safeBig(watch('buyOrder.entries.amount')).gt(0) && (
        <div>
          {formatNumberWithSuffix(watch('buyOrder.entries.amount'), {
            precision: 1,
          })}
        </div>
      )}
    </div>
  );

  if (!isDesktop) {
    return (
      <form
        autoComplete="off"
        className={twMerge(isFetching && 'animate-pulse')}
        onSubmit={(e) => {
          void onSubmit(e);
        }}
      >
        <FormProvider {...form}>
          <div className="h-full xxs:pb-10">
            <div className="flex xxs:space-x-1  items-center h-10 border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-solid xxs:border-dex-black-700">
              <TabsButton label="Trade" className="xxs:w-full lg:w-auto" />
              <TabsButton
                label="Info"
                className="xxs:w-full lg:w-auto"
                keyValue={AnalyticsKeys.Info}
              />
              <TabsButton
                label="Swaps"
                className="xxs:w-full lg:w-auto"
                keyValue={AnalyticsKeys.SmartSwaps}
              />
              {contract.generalInfo.chain.key === Chains.Solana && (
                <TabsButton
                  className="xxs:w-full lg:w-auto"
                  extraContent={
                    mainData?.numberOfHolders
                      ? ` (${formatNumberWithSuffix(
                          safeBig(mainData.numberOfHolders),
                          { precision: 0 }
                        )})`
                      : ' (--)'
                  }
                  label="Holders"
                  keyValue={AnalyticsKeys.Holders}
                />
              )}
            </div>
            <div className="flex flex-col	 lg:overflow-y-auto">
              {!queryParams.activeAnalytics && (
                <div className="xxs:flex space-x-1 items-center">
                  <TokenNameHeaderMobile
                    selectedPool={selectedPool}
                    contract={contract}
                    hotTokens={hotTokens}
                    isTerminal={true}
                  />
                </div>
              )}
              {!queryParams.activeAnalytics && (
                <SwapsFilterProvider>
                  <TokenInfoTradeBody
                    hotTokens={hotTokens}
                    contract={contract}
                    selectedPool={selectedPool}
                  />
                </SwapsFilterProvider>
              )}
            </div>

            <div className="w-full space-y-1 flex flex-col border-tailwind xxs:border-y-0 xxs:border-l-0 lg:border-l-1 xxs:border-r-0 xxs:border-dex-black-700 overflow-hidden">
              <div className=" flex flex-col overflow-y-auto ">
                {!queryParams.activeAnalytics && (
                  <TradeForm
                    allTemplates={allTemplates}
                    sniped={false}
                    contract={contract}
                    cta={
                      user ? (
                        !wallets?.length ? (
                          <div className="fixed left-0 right-0 bottom-0 xxs:px-2 xxs:py-4 w-full flex">
                            <PrimaryButton
                              className="xxs:w-full lg:w-auto lg:!mt-auto xxs:text-base lg:text-sm xxs:mx-2 "
                              size={isDesktop ? 'default' : 'large'}
                              textClassName="truncate"
                              type="button"
                              onClick={() => {
                                const searchParams = new URLSearchParams();
                                searchParams.set('isCreateOpen', 'true');
                                history.push({
                                  pathname: '/dex/wallets/portfolio',
                                  search: searchParams.toString(),
                                  state: {
                                    from: `${history.location.pathname}${history.location.search}`,
                                  },
                                });
                              }}
                            >
                              {buyAmount}
                            </PrimaryButton>
                          </div>
                        ) : (
                          <div className="xxs:flex xxs:flex-col xxs:space-y-2 lg:space-y-1">
                            <div className=" xxs:px-2 lg:px-0 flex flex-col xxs:space-y-1">
                              {contract.status === 'RUG_PULL' && (
                                <div className="xxs:pb-2 xxs:px-2">
                                  <NotificationDex
                                    type="error"
                                    size={isDesktop ? 'xSmall' : 'default'}
                                  >
                                    This token performed a rug-pull and can't be
                                    traded.
                                  </NotificationDex>{' '}
                                </div>
                              )}
                              {contract.tradingInfo.honeypot && !isDesktop && (
                                <div className="xxs:pb-2 xxs:px-2">
                                  <NotificationDex
                                    type="error"
                                    size={isDesktop ? 'xSmall' : 'default'}
                                  >
                                    Careful: this token looks like an honeypot!
                                  </NotificationDex>
                                </div>
                              )}

                              {watch('chain') === Chains.Solana &&
                                ((watch('takeProfitConfig.antiMev') &&
                                  watch('takeProfitConfig.bribe') > 0.1) ||
                                  (watch('buyOrder.entries.antiMev') &&
                                    watch('buyOrder.entries.bribe') > 0.1) ||
                                  watch('buyOrder.entries.maxBuyGasPriority') >
                                    0.1 ||
                                  watch('takeProfitConfig.maxSellGasPriority') >
                                    0.1) && (
                                  <div className="xxs:pb-2 xxs:px-2">
                                    <NotificationDex type="error">
                                      Warning, high gas fee! Review your
                                      settings.
                                    </NotificationDex>
                                  </div>
                                )}

                              <div className="fixed left-0 right-0 bottom-0 xxs:px-2 xxs:py-4 w-full flex">
                                <PrimaryButton
                                  disabled={disabled || !!isBlackListed}
                                  className="xxs:w-full lg:w-auto lg:!mt-auto xxs:text-base lg:text-sm xxs:mx-2 "
                                  loading={isFetching || isShortLoading}
                                  size={isDesktop ? 'default' : 'large'}
                                  textClassName="truncate"
                                  type="submit"
                                >
                                  {buyAmount}
                                </PrimaryButton>
                              </div>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className="fixed left-0 right-0 bottom-0 xxs:px-2 xxs:py-4 w-full flex">
                          <Button
                            variant="primary"
                            as={Link}
                            to={getAuthLoginUrl()}
                            className="w-full lg:!mt-auto xxs:mx-2 "
                            size={isDesktop ? 'default' : 'large'}
                            type="button"
                          >
                            {buyAmount}
                          </Button>
                        </div>
                      )
                    }
                  />
                )}
              </div>
            </div>
            {queryParams.activeAnalytics === AnalyticsKeys.SmartSwaps && (
              <SwapsFilterProvider>
                <SwapsTerminal contract={contract} />
              </SwapsFilterProvider>
            )}

            {queryParams.activeAnalytics === AnalyticsKeys.Info && (
              <MainInfoComponent hotTokens={hotTokens} contract={contract} />
            )}

            {queryParams.activeAnalytics === AnalyticsKeys.Holders && (
              <HoldersComponent
                mainData={mainData}
                error={error}
                isLoading={isLoading}
                contract={contract}
              />
            )}
          </div>
        </FormProvider>
      </form>
    );
  }
  return (
    <form
      autoComplete="off"
      className={twMerge(isFetching && 'animate-pulse')}
      onSubmit={(e) => {
        void onSubmit(e);
      }}
    >
      <FormProvider {...form}>
        <div className="flex ">
          <div className="flex-1 min-w-0 space-y-1 flex flex-col overflow-hidden">
            <div className="lg:h-terminalNew flex flex-col lg:overflow-y-auto">
              <SwapsFilterProvider>
                <TokenInfoTradeBody
                  hotTokens={hotTokens}
                  contract={contract}
                  selectedPool={selectedPool}
                />
              </SwapsFilterProvider>
            </div>
          </div>

          <div
            className={twMerge(
              isDesktop ? 'border-l-1' : 'border-l-0',
              'w-full lg:flex-none lg:w-[320px] flex flex-col border-tailwind xxs:border-y-0 xxs:border-r-0 xxs:border-dex-black-700 overflow-hidden h-terminalNew'
            )}
          >
            <div className=" flex flex-col lg:overflow-y-auto ">
              <div className=" h-[60px]">
                <MainStatsWrapperV2
                  contractPrices={contractPrices}
                  contract={contract}
                />
              </div>

              {isCreateOpen && (
                <CreateDWalletModal
                  selectedChain={contract.generalInfo.chain.key}
                  handleClose={close}
                />
              )}
              {!!(hotTokens?.[0] && isTokenSuspiciousCheck(hotTokens[0])) && (
                <div className="xxs:p-2 xxs:hidden lg:block border-tailwind xxs:border-x-0 xxs:border-t-0 xxs:border-dex-black-700">
                  <NotificationDex type="error" size="xSmall">
                    Warning: this token looks suspicious
                  </NotificationDex>
                </div>
              )}
              {!!user && (
                <TemplatesBar
                  chain={contract.generalInfo.chain.key}
                  templates={allTemplates}
                />
              )}

              <TradeForm
                allTemplates={allTemplates}
                sniped={false}
                contract={contract}
                cta={
                  user ? (
                    !wallets?.length ? (
                      <div className=" xxs:px-2 lg:px-0 flex flex-col xxs:space-y-1">
                        <PrimaryButton
                          onClick={open}
                          disabled={disabled}
                          className="xxs:w-auto lg:!mt-auto xxs:text-base lg:text-sm xxs:mx-2 "
                          size={isDesktop ? 'default' : 'large'}
                          textClassName="truncate"
                          type="button"
                        >
                          {buyAmount}
                        </PrimaryButton>
                      </div>
                    ) : (
                      <div className="xxs:flex xxs:flex-col xxs:space-y-2 lg:space-y-1">
                        <div className=" xxs:px-2 lg:px-0 flex flex-col xxs:space-y-1">
                          {contract.status === 'RUG_PULL' && (
                            <div className="xxs:pb-2 xxs:px-2">
                              <NotificationDex
                                type="error"
                                size={isDesktop ? 'xSmall' : 'default'}
                              >
                                This token performed a rug-pull and can't be
                                traded.
                              </NotificationDex>{' '}
                            </div>
                          )}
                          {contract.tradingInfo.honeypot && !isDesktop && (
                            <div className="xxs:pb-2 xxs:px-2">
                              <NotificationDex
                                type="error"
                                size={isDesktop ? 'xSmall' : 'default'}
                              >
                                Careful: this token looks like an honeypot!
                              </NotificationDex>
                            </div>
                          )}

                          {watch('chain') === Chains.Solana &&
                            ((watch('takeProfitConfig.antiMev') &&
                              watch('takeProfitConfig.bribe') > 0.1) ||
                              (watch('buyOrder.entries.antiMev') &&
                                watch('buyOrder.entries.bribe') > 0.1) ||
                              watch('buyOrder.entries.maxBuyGasPriority') >
                                0.1 ||
                              watch('takeProfitConfig.maxSellGasPriority') >
                                0.1) && (
                              <div className="xxs:pb-2 xxs:px-2">
                                <NotificationDex type="error">
                                  Warning, high gas fee! Review your settings.
                                </NotificationDex>
                              </div>
                            )}

                          <PrimaryButton
                            disabled={disabled || !!isBlackListed}
                            className="xxs:w-auto lg:!mt-auto xxs:text-base lg:text-sm xxs:mx-2 "
                            loading={isFetching || isShortLoading}
                            size={isDesktop ? 'default' : 'large'}
                            textClassName="truncate"
                            type="submit"
                          >
                            {buyAmount}
                          </PrimaryButton>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="flex">
                      <Button
                        variant="primary"
                        as={Link}
                        to={getAuthLoginUrl()}
                        className="w-full lg:!mt-auto xxs:mx-2 "
                        size={isDesktop ? 'default' : 'large'}
                        type="button"
                      >
                        {buyAmount}
                      </Button>
                    </div>
                  )
                }
              />
              <div className="border-tailwind xxs:border-x-0 xxs:border-b-0 border-dex-black-700">
                <MainInfoBody hotTokens={hotTokens} contract={contract} />
              </div>
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  );
}
